// ============================================
// Reusable invoice upload card. Plugs into any form
// whose backend exposes POST/GET/DELETE .../invoice endpoints.
// ============================================
import React, { useRef, useState } from 'react';
import { Loader2, FileText, FileUp, Trash2 } from 'lucide-react';
import apiClient from '../api/apiClient';
import { num } from '../types/markfed';

interface InvoiceAPI {
  uploadInvoice: (seasonId: number, id: number, file: File) => Promise<{ data: any }>;
  invoiceUrl: (seasonId: number, id: number) => string;
  deleteInvoice: (seasonId: number, id: number) => Promise<{ data: any }>;
}

interface Props {
  seasonId: number;
  recordId: number;
  api: InvoiceAPI;
  /** Current record fields — the card reads invoice_* fields from here. */
  record: any;
  /** Called with the updated record (backend response) after upload/delete. */
  onChange: (updated: any) => void;
  /** When true, hide destructive actions (replace/delete). Upload still allowed only if record has no invoice. */
  locked?: boolean;
  /** Shown as card heading. */
  label?: string;
  /** Helper text below heading. */
  description?: string;
}

export const InvoiceUploadCard: React.FC<Props> = ({
  seasonId, recordId, api, record, onChange, locked, label = 'Invoice', description,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    setError(null);
    try {
      const res = await api.uploadInvoice(seasonId, recordId, file);
      onChange(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async () => {
    try {
      const res = await apiClient.get(api.invoiceUrl(seasonId, recordId), { responseType: 'blob' });
      const blob = new Blob([res.data], { type: (res.headers as any)['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to open');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove the uploaded invoice?')) return;
    setError(null);
    try {
      const res = await api.deleteInvoice(seasonId, recordId);
      onChange({ ...res.data, invoice_file_path: null, invoice_file_name: null, invoice_uploaded_at: null });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove');
    }
  };

  const hasInvoice = !!record?.invoice_file_name;

  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-blue-600" />
        {label}
      </h3>
      {description && <p className="text-xs text-gray-500 mb-3">{description}</p>}
      {error && (
        <div className="mb-3 text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2">{error}</div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        onChange={handleSelect}
        className="hidden"
      />
      {hasInvoice ? (
        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{record.invoice_file_name}</p>
              <p className="text-xs text-gray-500">
                {record.invoice_size_bytes ? `${(num(record.invoice_size_bytes) / 1024).toFixed(1)} KB · ` : ''}
                Uploaded {record.invoice_uploaded_at ? new Date(record.invoice_uploaded_at).toLocaleString('en-IN') : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleView}
              className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-lg"
            >
              View
            </button>
            {!locked && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Replace'}
                </button>
                <button
                  onClick={handleDelete}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Remove invoice"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || locked}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
          {uploading ? 'Uploading...' : 'Upload Invoice'}
        </button>
      )}
    </div>
  );
};

export default InvoiceUploadCard;
